// [목적] 브라우저 Canvas API 로 이미지를 재인코딩해 EXIF(GPS·촬영시각 등) 메타데이터를 제거하고,
//        긴 변 기준 상한을 넘으면 비율을 유지한 채 축소한 새 File 을 반환한다.
// [이유] 보안 리뷰 docs/security/2026-09-18_storage-and-image-upload.md M-2 — 아바타/커버 업로드는
//        presigned PUT 으로 브라우저→S3 직행이라 서버가 업로드 바이트를 가공할 지점이 없다.
//        <canvas>로 픽셀만 다시 그려 인코딩하면 원본 파일의 EXIF 블록이 결과물에 남지 않는다
//        (표준 브라우저 canvas.toBlob 인코더는 EXIF 를 새로 쓰지 않음).
// [사이드 이팩트] 없음(순수 함수 — 호출부가 반환된 File 의 실제 size/type 으로 presign 요청 + PUT 해야 함).
// [수정 시 고려사항] createImageBitmap/HTMLCanvasElement.toBlob 은 jsdom 에 없어 단위 테스트는
//                   vi.stubGlobal 로 최소 스텁한다. 실패 원인(unsupported-type/decode-failed)을
//                   StripImageExifError.reason 으로 구분해 호출부가 사용자 메시지를 분기하게 한다.
//                   디코드/인코딩 실패 시 원본 그대로 업로드하는 폴백은 EXIF 가 남으므로 절대 금지 —
//                   실패하면 항상 업로드를 차단해야 한다.

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
type AllowedMimeType = (typeof ALLOWED_MIME_TYPES)[number];

export type StripImageExifFailureReason = 'unsupported-type' | 'decode-failed';

/**
 * [목적] stripImageExif 실패 사유를 구분해 호출부가 사용자 안내 문구를 분기하도록 하는 에러 타입
 * [이유] "지원하지 않는 형식"과 "손상된 파일이라 디코드 실패"는 사용자에게 서로 다른 안내가 필요하다.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] reason 추가 시 호출부(ProfileEditScreen) 분기도 함께 갱신할 것.
 */
export class StripImageExifError extends Error {
  constructor(
    public readonly reason: StripImageExifFailureReason,
    message: string,
  ) {
    super(message);
    this.name = 'StripImageExifError';
  }
}

function isAllowedMime(type: string): type is AllowedMimeType {
  return (ALLOWED_MIME_TYPES as readonly string[]).includes(type);
}

/**
 * [목적] 이미지 파일을 canvas 로 재인코딩해 EXIF 제거 + 긴 변 상한 축소된 새 File 을 반환
 * [이유] 보안 리뷰 M-2 — presigned PUT 발급 전 FE 단에서 EXIF(GPS) 를 제거할 수 있는 유일한 지점.
 * [사이드 이팩트] 없음(입력 File 은 변경하지 않는 순수 함수).
 * [수정 시 고려사항] 출력 MIME 은 입력과 동일 유지(jpeg/webp 는 quality 적용, png 는 무손실 포맷이라
 *                   브라우저가 quality 인자를 무시함 — 표준 동작이므로 별도 분기 불필요).
 *                   maxDimension 은 kind(avatar/cover)별 상한을 호출부가 결정해 전달한다.
 */
export async function stripImageExif(
  file: File,
  maxDimension: number,
  quality = 0.9,
): Promise<File> {
  if (!isAllowedMime(file.type)) {
    throw new StripImageExifError(
      'unsupported-type',
      `지원하지 않는 이미지 형식입니다: ${file.type || '(알 수 없음)'}`,
    );
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new StripImageExifError('decode-failed', '이미지를 디코드할 수 없습니다 (손상된 파일).');
  }

  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
  const outWidth = Math.max(1, Math.round(bitmap.width * scale));
  const outHeight = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = outWidth;
  canvas.height = outHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close?.();
    throw new StripImageExifError(
      'decode-failed',
      '이미지를 렌더링할 캔버스 컨텍스트를 얻지 못했습니다.',
    );
  }
  ctx.drawImage(bitmap, 0, 0, outWidth, outHeight);
  bitmap.close?.();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, file.type, quality);
  });
  if (!blob) {
    throw new StripImageExifError('decode-failed', '이미지 인코딩에 실패했습니다.');
  }

  return new File([blob], file.name, { type: file.type, lastModified: Date.now() });
}
